import { TypedDocumentNode, gql } from '@apollo/client';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Card } from '@sorare/core/src/components/card/Card';
import { Score } from '@sorare/core/src/components/collections/Score';
import { OpenItemDialogLink } from '@sorare/core/src/components/link/OpenItemDialogLink';
import { ANY_SPORT_CARD_SHOW } from '@sorare/core/src/constants/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useDeeplinkFromNativeApp } from '@sorare/core/src/hooks/useDeeplinkFromNativeApp';
import { isMyCardListedOnMarket } from '@sorare/core/src/lib/cards';

import { useClickCardEvent } from '@sorare/marketplace/src/hooks/events/useClickCardEvent';

import CardPreview from 'components/collections/CardPreview';

import {
  FulfilledSlot_cardCollection,
  FulfilledSlot_userCardCollectionSlot,
} from './__generated__/index.graphql';

const Wrapper = styled(Vertical).attrs({ center: true })`
  justify-content: flex-end;
`;
const StyledOpenItemDialogLink = styled(OpenItemDialogLink)`
  width: 100%;
`;

type Props = {
  bannerPictureUrl: string;
  userSlot: FulfilledSlot_userCardCollectionSlot;
  cardCollection?: FulfilledSlot_cardCollection;
};
export const FulfilledSlot = ({
  bannerPictureUrl,
  userSlot,
  cardCollection,
}: Props) => {
  const { isWebviewFromNativeApp, navigateWithDeeplink } =
    useDeeplinkFromNativeApp();
  const { generateSportContextPath } = useSportContext();
  const [dialogOpen, setDialogOpen] = useState(false);
  const { highlightedCardCollectionCard } = userSlot;
  const trackClickCard = useClickCardEvent();
  if (!highlightedCardCollectionCard) return null;

  const { anyCard: card, scoreBreakdown } = highlightedCardCollectionCard;

  const cardListed = isMyCardListedOnMarket(card);

  const cardNode = <Card card={card} width={320} />;

  return (
    <Wrapper>
      {isWebviewFromNativeApp ? (
        <button
          type="button"
          onClick={() =>
            navigateWithDeeplink(
              generateSportContextPath(ANY_SPORT_CARD_SHOW, {
                params: {
                  slug: card.slug,
                },
              })
            )
          }
        >
          {cardNode}
        </button>
      ) : (
        <StyledOpenItemDialogLink
          item={card}
          sport={Sport.FOOTBALL}
          onClick={() => {
            trackClickCard(card, false);
          }}
        >
          {cardNode}
        </StyledOpenItemDialogLink>
      )}
      <Button
        size="small"
        color="quaternary"
        onClick={() => setDialogOpen(true)}
      >
        {cardListed && (
          <FontAwesomeIcon
            color="var(--c-yellow-600)"
            icon={faExclamationCircle}
          />
        )}
        <Score score={scoreBreakdown.total} />
      </Button>
      <CardPreview
        bannerPictureUrl={bannerPictureUrl}
        cardCollectionCard={highlightedCardCollectionCard}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        cardCollection={cardCollection}
      />
    </Wrapper>
  );
};

FulfilledSlot.fragments = {
  userCardCollectionSlot: gql`
    fragment FulfilledSlot_userCardCollectionSlot on UserCardCollectionSlot {
      slug
      highlightedCardCollectionCard {
        id
        scoreBreakdown {
          total
        }
        anyCard {
          slug
          pictureUrl
          ...isMyCardListedOnMarket_anyCard
          ...Card_anyCard
          ...useClickCardEvent_anyCard
        }
        ...CardPreview_cardCollectionCard
      }
      slot {
        id
        player {
          slug
          displayName
        }
      }
    }
    ${CardPreview.fragments.cardCollectionCard}
    ${isMyCardListedOnMarket.fragments.anyCard}
    ${Card.fragments.anyCard}
    ${useClickCardEvent.fragments.anyCard}
  ` as TypedDocumentNode<FulfilledSlot_userCardCollectionSlot>,
  cardCollection: gql`
    fragment FulfilledSlot_cardCollection on CardCollection {
      slug
      ...CardPreview_cardCollection
    }
    ${CardPreview.fragments.cardCollection}
  ` as TypedDocumentNode<FulfilledSlot_cardCollection>,
};
